import React, { useState } from 'react';
import { supabase } from '../supabase';
import { jsPDF } from 'jspdf';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

const resolutionInfo = {
  annullata_libera: {
    icon: '✅',
    title: 'Prenotazione annullata',
    text: 'Cancellazione libera (oltre 48h prima del lavoro) — rimborso completo, nessuna commissione trattenuta.',
  },
  annullata_worker: {
    icon: '✅',
    title: 'Prenotazione annullata dal lavoratore',
    text: 'Il lavoratore ha cancellato entro le 48h — rimborso completo al committente, nessuna commissione trattenuta.',
  },
  risolta_rifiutata: {
    icon: '⚠️',
    title: 'Cancellazione respinta',
    text: 'Il lavoratore ha rifiutato la cancellazione tardiva — ha ricevuto il compenso pieno pattuito come indennizzo.',
  },
  risolta_accettata: {
    icon: '↩️',
    title: 'Cancellazione accettata',
    text: 'Il lavoratore ha accettato la cancellazione tardiva — il committente è stato rimborsato (la commissione Jobbetty resta trattenuta).',
  },
};

// Mappa resolution -> nome evento di tracking, per evitare if/else ripetuti nei punti di chiamata
const resolutionToEventName = {
  annullata_libera: 'cancellazione_libera',
  annullata_worker: 'cancellazione_lavoratore',
  risolta_rifiutata: 'cancellazione_tardiva_lavoratore_rifiuta',
  risolta_accettata: 'cancellazione_tardiva_lavoratore_accetta',
};

function generaRicevutaIndennizzo(application, profile, session) {
  const doc = new jsPDF();
  const oggi = new Date().toLocaleDateString('it-IT');
  const nomeLavoratore = profile?.full_name || session?.user?.email;
  const importo = application?.amount_paid;

  doc.setFontSize(22);
  doc.setTextColor(45, 122, 58);
  doc.text('Jobbetty', 105, 20, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('RICEVUTA DI INDENNIZZO PER CANCELLAZIONE TARDIVA', 105, 32, { align: 'center' });

  doc.setLineWidth(0.5);
  doc.setDrawColor(45, 122, 58);
  doc.line(20, 38, 190, 38);

  doc.setFontSize(11);
  doc.text(`Data: ${oggi}`, 20, 50);
  doc.text(`Beneficiario: ${nomeLavoratore}`, 20, 62);
  doc.text(`Email: ${session?.user?.email}`, 20, 74);

  doc.line(20, 82, 190, 82);

  doc.setFontSize(12);
  doc.setTextColor(45, 122, 58);
  doc.text('DETTAGLI', 20, 94);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.text(`Prenotazione: ${application?.jobs?.title || 'Prenotazione occasionale'}`, 20, 106);
  doc.text(`Categoria: ${application?.jobs?.category || '-'}`, 20, 118);
  doc.text(`Importo indennizzo: EUR ${importo?.toFixed(2)}`, 20, 130);

  doc.line(20, 140, 190, 140);

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const noteLines = doc.splitTextToSize(
    "Questo importo rappresenta un indennizzo per cancellazione tardiva da parte del committente (oltre la soglia delle 48h), non il corrispettivo di una prestazione svolta. La prestazione non ha avuto luogo.",
    170
  );
  doc.text(noteLines, 20, 154);

  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text('Documento generato da Jobbetty', 105, 280, { align: 'center' });

  doc.save(`indennizzo_cancellazione_${oggi.replace(/\//g, '-')}.pdf`);
}

function CancellationCard({ application, isApplicant, conversationId, session, profile, otherPersonId, otherPerson, onUpdate }) {
  const trackEvent = useTrackEvent();
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const jobStartDate = application?.selected_dates?.[0] || null;

  const insertSystemMessage = async (content) => {
    await supabase.from('messages').insert([{
      conversation_id: conversationId,
      sender_id: session.user.id,
      content,
      read: false,
      type: 'text',
    }]);
  };

  const handleRequestCancellation = async () => {
    const confirmText = 'Sei sicuro di voler annullare questa prenotazione?';
    if (!window.confirm(confirmText)) return;

    setLoading(true);
    try {
      const requesterRole = isApplicant ? 'worker' : 'committente';
      const { data, error } = await supabase.functions.invoke('request-cancellation', {
        body: {
          application_id: application.id,
          requested_by: session.user.id,
          requester_role: requesterRole,
          job_start_date: jobStartDate,
        },
      });

      if (error || data?.error) {
        alert('Errore durante la richiesta di cancellazione. Riprova.');
        return;
      }

      const resolution = data.resolution;

      if (resolution === 'richiesta_committente') {
        await insertSystemMessage('⚠️ Il committente ha richiesto la cancellazione (meno di 48h dal lavoro). In attesa della risposta del lavoratore.');
        onUpdate({ cancellation_status: 'richiesta_committente' });
        if (otherPersonId) {
          sendPushNotification({
            userId: otherPersonId,
            title: '⚠️ Richiesta di cancellazione',
            body: 'Il committente vuole cancellare la prenotazione — la tua conferma è richiesta',
            url: `/chat/${conversationId}`,
          });
        }
      } else {
        // Risoluzione immediata (nessun ok richiesto all'altra parte):
        // "annullata_libera" (>48h, qualsiasi parte) oppure "annullata_worker" (lavoratore cancella <48h)
        const info = resolutionInfo[resolution];
        await insertSystemMessage(`${info.icon} ${info.title} — ${info.text}`);
        onUpdate({ status: 'cancellata', cancellation_status: resolution });

        const eventName = resolutionToEventName[resolution];
        if (eventName) {
          trackEvent(eventName, {
            conversation_id: conversationId,
            application_id: application.id,
            requester_role: requesterRole,
          });
        }

        if (otherPersonId) {
          sendPushNotification({
            userId: otherPersonId,
            title: `${info.icon} ${info.title}`,
            body: info.text,
            url: `/chat/${conversationId}`,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWorkerResponse = async (workerResponse) => {
    const confirmText = workerResponse === 'accetta'
      ? 'Confermi di accettare la cancellazione? Non riceverai il compenso.'
      : 'Confermi di rifiutare la cancellazione? Riceverai comunque il compenso pieno pattuito.';
    if (!window.confirm(confirmText)) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('respond-cancellation', {
        body: {
          application_id: application.id,
          worker_response: workerResponse,
        },
      });

      if (error || data?.error) {
        alert('Errore durante la risposta. Riprova.');
        return;
      }

      const resolution = data.resolution;
      const info = resolutionInfo[resolution];
      await insertSystemMessage(`${info.icon} ${info.title} — ${info.text}`);
      onUpdate({ status: 'cancellata', cancellation_status: resolution });

      // "risolta_rifiutata" (lavoratore rifiuta, riceve indennizzo) oppure
      // "risolta_accettata" (lavoratore accetta, non riceve nulla)
      const eventName = resolutionToEventName[resolution];
      if (eventName) {
        trackEvent(eventName, {
          conversation_id: conversationId,
          application_id: application.id,
          amount_paid: application?.amount_paid ?? null,
        });
      }

      if (otherPersonId) {
        sendPushNotification({
          userId: otherPersonId,
          title: `${info.icon} ${info.title}`,
          body: info.text,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Stato risolto/terminale — banner informativo
  if (application?.status === 'cancellata' && application?.cancellation_status) {
    const info = resolutionInfo[application.cancellation_status];
    if (!info) return null;

    const showIndennizzo = application.cancellation_status === 'risolta_rifiutata' && isApplicant;

    return (
      <div style={{
        background: theme.colors.backgroundAlt, borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.border}`, padding: theme.spacing.lg,
        margin: `0 0 ${theme.spacing.lg}px`, textAlign: 'center'
      }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>{info.icon}</div>
        <div style={{ fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.textPrimary, marginBottom: 4 }}>{info.title}</div>
        <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, lineHeight: 1.5, marginBottom: showIndennizzo ? theme.spacing.md : 0 }}>{info.text}</div>

        {showIndennizzo && (
          <>
            <div style={{ fontSize: 28, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.md }}>
              +€{application.amount_paid?.toFixed(2)}
            </div>
            <button
              onClick={() => generaRicevutaIndennizzo(application, profile, session)}
              style={{ background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: '8px 16px', cursor: 'pointer', fontSize: theme.font.size.sm, fontWeight: 700 }}>
              📄 Scarica ricevuta
            </button>
          </>
        )}
      </div>
    );
  }

  // Non applicabile: lavoro già completato, o non ancora accettato
  if (application?.status !== 'accettata' || application?.completed) return null;

  // Richiesta pendente del committente
  if (application?.cancellation_status === 'richiesta_committente') {
    if (isApplicant) {
      return (
        <div style={{
          background: theme.colors.warningBgSoft, borderRadius: theme.radius.lg,
          border: `1px solid ${theme.colors.warningBorder}`, padding: theme.spacing.lg,
          margin: `0 0 ${theme.spacing.lg}px`
        }}>
          <div style={{ fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.warningText, marginBottom: 6 }}>
            ⚠️ Richiesta di cancellazione
          </div>
          <p style={{ margin: '0 0 12px', fontSize: theme.font.size.base, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
            Il committente ha chiesto di cancellare la prenotazione (meno di 48h dal lavoro). Puoi rifiutare e ricevere comunque il compenso pieno, oppure accettare e cancellare senza compenso.
          </p>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <button onClick={() => handleWorkerResponse('rifiuta')} disabled={loading}
              style={{ flex: 1, background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.base, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '⏳...' : '❌ Rifiuta (ricevo il compenso)'}
            </button>
            <button onClick={() => handleWorkerResponse('accetta')} disabled={loading}
              style={{ flex: 1, background: theme.colors.white, color: theme.colors.textSecondary, border: `1px solid ${theme.colors.borderInput}`, borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.base, cursor: loading ? 'not-allowed' : 'pointer' }}>
              ✅ Accetta cancellazione
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={{
        background: theme.colors.warningBgSoft, borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.warningBorder}`, padding: theme.spacing.lg,
        margin: `0 0 ${theme.spacing.lg}px`, textAlign: 'center'
      }}>
        <div style={{ fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.warningText, marginBottom: 4 }}>
          ⏳ Richiesta di cancellazione inviata
        </div>
        <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
          In attesa della risposta del lavoratore...
        </div>
      </div>
    );
  }

  // Nessuna richiesta attiva — link discreto per avviare la cancellazione
  if (!expanded) {
    return (
      <div style={{ textAlign: 'center', margin: `0 0 ${theme.spacing.md}px` }}>
        <button onClick={() => setExpanded(true)}
          style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: theme.font.size.sm, cursor: 'pointer', textDecoration: 'underline' }}>
          Annulla prenotazione
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: theme.colors.background, borderRadius: theme.radius.lg,
      border: `1px solid ${theme.colors.border}`, padding: theme.spacing.lg,
      margin: `0 0 ${theme.spacing.lg}px`
    }}>
      <p style={{ margin: '0 0 12px', fontSize: theme.font.size.base, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
        Oltre 48h prima del lavoro la cancellazione è libera e il rimborso è totale. Sotto le 48h si applicano regole diverse a seconda di chi cancella.
      </p>
      <div style={{ display: 'flex', gap: theme.spacing.sm }}>
        <button onClick={() => setExpanded(false)} disabled={loading}
          style={{ flex: 1, background: theme.colors.white, color: theme.colors.textSecondary, border: `1px solid ${theme.colors.borderInput}`, borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.base, cursor: 'pointer' }}>
          Indietro
        </button>
        <button onClick={handleRequestCancellation} disabled={loading}
          style={{ flex: 2, background: theme.colors.errorDark, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 10, fontSize: theme.font.size.base, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? '⏳ Elaborazione...' : '🚫 Conferma annullamento'}
        </button>
      </div>
    </div>
  );
}

export default CancellationCard;